import { useMemo, useState } from 'react';
import { useQuery } from 'convex/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import {
  ACHIEVEMENTS,
  ACHIEVEMENT_CATEGORIES,
  COMMUNITY_CHALLENGE_ACTIVE,
} from '../../../shared/achievements';
import WordSplitDonut from './WordSplitDonut';
import AchievementCard from './AchievementCard';
import './RewardsPage.css';

function RewardsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const progress = useQuery(api.achievements.getProgress);
  const userAchievements = useQuery(api.achievements.listForUser);
  const creditEvents = useQuery(api.credits.listEvents);

  const [activeTab, setActiveTab] = useState('overview');

  const unlockedMap = useMemo(() => {
    if (!userAchievements) return new Map();
    return new Map(userAchievements.map((a) => [a.achievementId, a]));
  }, [userAchievements]);

  const getCurrentValue = (achievement) => {
    if (!progress) return 0;
    switch (achievement.category) {
      case 'volume':
        return progress.totalUserWords;
      case 'streaks':
        return progress.currentStreak;
      case 'project':
        if (achievement.id === 'first-project' || achievement.id === 'three-projects') return progress.projectCount;
        if (achievement.id === 'first-chapter') return progress.chapterCount;
        if (achievement.id === 'first-review') return progress.completedReviews;
        if (achievement.id === 'first-audience') return progress.completedAudienceTests;
        if (achievement.id === 'first-bible') return progress.storyBibleCount;
        return 0;
      case 'community':
        if (achievement.id === 'first-post') return progress.postCount;
        if (achievement.id === 'first-challenge' || achievement.id === 'three-challenges') return progress.challengeSubmissionCount;
        if (achievement.id === 'first-comment-received') return progress.commentsReceived;
        if (achievement.id === 'ten-followers' || achievement.id === 'fifty-followers') return progress.followerCount;
        return 0;
      default:
        return 0;
    }
  };

  if (progress === undefined || userAchievements === undefined) {
    return <main className="rw-page rw-loading">{t('rewards.loading')}</main>;
  }

  const unlockedCount = userAchievements?.length ?? 0;
  const categoryLabels = {
    volume: t('rewards.categories.volume'),
    project: t('rewards.categories.project'),
    community: t('rewards.categories.community'),
    streaks: t('rewards.categories.streaks'),
  };

  return (
    <main className="rw-page" aria-label={t('rewards.ariaLabel')}>

      <header className="rw-header">
        <div>
          <h1>{t('rewards.title')}</h1>
          <p className="rw-header-desc">{t('rewards.subtitle')}</p>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/projects')}>{t('common.back')}</button>
      </header>

      {COMMUNITY_CHALLENGE_ACTIVE && (
        <div className="rw-challenge">
          <div>
            <span className="rw-challenge__text">{t('rewards.challengeProgressText')}</span>
            <span className="rw-challenge__sub">
              {t('rewards.challengeProgressSub', {
                submitted: progress.challengeSubmissionCount ?? 0,
                saved: progress.savedChallengeCount ?? 0,
              })}
            </span>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/projects/challenges')}>
            {t('rewards.challengeProgressCta')}
          </button>
        </div>
      )}

      <nav className="rw-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'overview'}
          className={`rw-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          {t('rewards.tabs.overview')}
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'history'}
          className={`rw-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          {t('rewards.tabs.history')}
        </button>
      </nav>

      <section className="rw-surface">

      {activeTab === 'overview' ? (
        <div className="rw-fade" role="tabpanel">

          {/* Stats */}
          <div className="rw-stats">
            <div className="rw-stat">
              <span className="rw-stat__label">{t('rewards.stats.yourWords')}</span>
              <strong className="rw-stat__value">{(progress.totalUserWords ?? 0).toLocaleString()}</strong>
              <span className="rw-stat__sub">{t('rewards.stats.writtenByHand')}</span>
            </div>
            <div className="rw-stat">
              <span className="rw-stat__label">{t('rewards.stats.aiWords')}</span>
              <strong className="rw-stat__value">{(progress.totalAiWords ?? 0).toLocaleString()}</strong>
              <span className="rw-stat__sub">{t('rewards.stats.aiAssisted')}</span>
            </div>
            <div className="rw-stat">
              <span className="rw-stat__label">{t('rewards.stats.currentStreak')}</span>
              <strong className="rw-stat__value">{progress.currentStreak}</strong>
              <span className="rw-stat__sub">{t('rewards.day', { count: progress.currentStreak })}</span>
            </div>
            <div className="rw-stat">
              <span className="rw-stat__label">{t('rewards.stats.creditsEarned')}</span>
              <strong className="rw-stat__value">{(progress.totalCreditsEarned ?? 0).toLocaleString()}</strong>
              <span className="rw-stat__sub">{t('rewards.stats.achievementCount', { count: unlockedCount })}</span>
            </div>
            <div className="rw-stat">
              <span className="rw-stat__label">{t('rewards.stats.challengeEntries')}</span>
              <strong className="rw-stat__value">{progress.challengeSubmissionCount ?? 0}</strong>
              <span className="rw-stat__sub">{t('rewards.stats.savedChallenges', { count: progress.savedChallengeCount ?? 0 })}</span>
            </div>
          </div>

          {/* Donut */}
          <div className="rw-donut-section">
            <WordSplitDonut
              userWords={progress.totalUserWords ?? 0}
              aiWords={progress.totalAiWords ?? 0}
            />
            <div className="rw-donut-legend">
              <div className="rw-donut-legend__item">
                <span className="rw-donut-legend__dot rw-donut-legend__dot--user" />
                <span className="rw-donut-legend__count">{(progress.totalUserWords ?? 0).toLocaleString()}</span>
                <span className="rw-donut-legend__label">{t('rewards.stats.wordsByYou')}</span>
              </div>
              <div className="rw-donut-legend__item">
                <span className="rw-donut-legend__dot rw-donut-legend__dot--ai" />
                <span className="rw-donut-legend__count">{(progress.totalAiWords ?? 0).toLocaleString()}</span>
                <span className="rw-donut-legend__label">{t('rewards.stats.wordsByAi')}</span>
              </div>
            </div>
          </div>

          {/* Achievement Grid by Category */}
          {ACHIEVEMENT_CATEGORIES.map((cat) => {
            const catAchievements = ACHIEVEMENTS.filter((a) => a.category === cat);
            if (catAchievements.length === 0) return null;
            return (
              <div key={cat} className="rw-ach-section">
                <span className="rw-ach-label">{categoryLabels[cat]}</span>
                <div className="rw-ach-grid">
                  {catAchievements.map((achievement) => {
                    const record = unlockedMap.get(achievement.id);
                    return (
                      <AchievementCard
                        key={achievement.id}
                        achievementId={achievement.id}
                        unlocked={!!record}
                        unlockedAt={record?.unlockedAt}
                        creditsAwarded={record?.creditsAwarded}
                        currentValue={getCurrentValue(achievement)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {activeTab === 'history' ? (
        <div className="rw-fade" role="tabpanel">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', marginBottom: 16 }}>{t('rewards.historyTitle')}</h2>
          {!creditEvents || creditEvents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--color-text-tertiary)' }}>
              <p>{t('rewards.noHistory')}</p>
            </div>
          ) : (
            <div className="rw-history-list">
              {creditEvents.map((event) => {
                const isPositive = event.type === 'adjustment';
                const desc = event.description
                  || (event.type === 'charge'
                    ? (event.modelId
                      ? t('rewards.aiUsage', { modelId: event.modelId })
                      : t('rewards.aiUsageGeneric'))
                    : t('rewards.creditRefill'));
                return (
                  <div key={event._id} className="rw-history-item">
                    <div className="rw-history-item__left">
                      <span className="rw-history-item__desc">{desc}</span>
                      <span className="rw-history-item__date">{new Date(event.createdAt).toLocaleString()}</span>
                    </div>
                    <span className={`rw-history-item__amount ${isPositive ? 'rw-history-item__amount--positive' : 'rw-history-item__amount--negative'}`}>
                      {isPositive ? '+' : '-'}{event.credits ?? 0}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      </section>
    </main>
  );
}

export default RewardsPage;
